import { supabase } from './supabaseClient';
import { WebsiteMessageService } from './websiteMessageService';
import { PreReleaseProject, ProjectInterest } from './preReleaseService';
import { emailClickTrackingService } from './emailClickTrackingService';
import { NotificationValidation, NotificationValidationError, NotificationServiceError, NotificationErrorRecovery } from '../utils/notificationValidation';

// Simple in-memory cache for performance optimization
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number; // time to live in milliseconds
}

class SimpleCache {
  private cache = new Map<string, CacheEntry<any>>();
  
  set<T>(key: string, data: T, ttlMinutes: number = 10): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl: ttlMinutes * 60 * 1000
    });
  }
  
  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;
    
    if (Date.now() - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return null;
    }
    
    return entry.data;
  }
  
  clear(): void {
    this.cache.clear();
  }
  
  delete(key: string): void {
    this.cache.delete(key);
  }
}

export interface InterestedUser {
  id: string;
  user_id: string;
  email: string;
  username: string;
  interest_level: number;
  willing_to_pay: boolean;
  committed_to_buy: boolean;
  early_bird_commitment?: boolean;
  max_price_willing?: number;
  created_at: string;
}

export interface EmailTemplateData {
  recipients: string[];
  subject: string;
  htmlContent: string;
  plainTextContent: string;
  trackingEnabled: boolean;
  templateId?: string;
  personalizedTemplates?: Map<string, string>;
  projectDetails: {
    title: string;
    description: string;
    imageUrl?: string;
    demoUrl?: string;
    pricing: string;
  };
}

export interface WebsiteMessageData {
  recipients: string[];
  messageText: string;
  messageType: 'project_release' | 'discount_offer' | 'update';
  projectId: string;
  includeProjectLink: boolean;
  customContent?: string;
}

export interface NotificationRecord {
  id: string;
  project_id: string;
  notification_type: 'email_template' | 'website_message';
  recipient_count: number;
  template_data?: any;
  message_content?: string;
  created_at: string;
  created_by: string;
}

export interface ContactHistoryRecord {
  id: string;
  project_id: string;
  user_id: string;
  contact_type: 'email_template' | 'website_message' | 'discount_offer';
  contact_date: string;
  message_content?: string;
  engagement_tracked: boolean;
  clicked_through: boolean;
  converted_to_purchase: boolean;
  metadata?: any;
}

export interface ContactHistoryWithUser extends ContactHistoryRecord {
  user: {
    id: string;
    username: string;
    email: string;
  };
}

class PostReleaseNotificationService {
  private static cache = new SimpleCache();
  
  /**
   * Get all users who expressed interest in a specific project (with caching)
   * Requirements: 1.1, 1.4, 2.1, 3.1, 5.1, 9.1, 9.2
   */
  async getInterestedUsers(projectId: string): Promise<InterestedUser[]> {
    try {
      // Validate input
      if (!projectId?.trim()) {
        throw new NotificationValidationError(
          'Project ID is required to fetch interested users',
          'PROJECT_ID_REQUIRED',
          'projectId'
        );
      }

      // Check cache first
      const cacheKey = `interested_users_${projectId}`;
      const cachedData = PostReleaseNotificationService.cache.get<InterestedUser[]>(cacheKey);
      if (cachedData) {
        return cachedData;
      }

      // Fetch data with retry mechanism
      const data = await NotificationErrorRecovery.retryDatabaseOperation(async () => {
        const { data, error } = await supabase
          .from('project_interest')
          .select(`
            *,
            user:user_id (
              id,
              email,
              username
            )
          `)
          .eq('project_id', projectId)
          .order('created_at', { ascending: false })
          .limit(1000); // Add limit for performance

        if (error) {
          throw error;
        }

        return data;
      });

      if (!data || data.length === 0) {
        return [];
      }

      // Transform and validate the data
      const validUsers = data
        .filter(item => {
          // Only include users with valid email and user data
          if (!item.user || !item.user.email) {
            console.warn(`Skipping user ${item.user_id} - missing user data or email`);
            return false;
          }
          return true;
        })
        .map(item => ({
          id: item.id,
          user_id: item.user_id,
          email: item.user.email,
          username: item.user.username || 'Unknown User',
          interest_level: item.interest_level || 1,
          willing_to_pay: item.willing_to_pay || false,
          committed_to_buy: item.committed_to_buy || false,
          early_bird_commitment: item.early_bird_commitment || false,
          max_price_willing: item.max_price_willing || null,
          created_at: item.created_at
        }));

      // Validate each user record
      validUsers.forEach((user, index) => {
        try {
          NotificationValidation.validateInterestedUser(user, index);
        } catch (validationError: any) {
          console.warn(`User validation failed for index ${index}:`, validationError.message);
          // Continue with other users instead of failing completely
        }
      });

      // Cache the results for 5 minutes
      PostReleaseNotificationService.cache.set(cacheKey, validUsers, 5);

      return validUsers;
    } catch (error: any) {
      if (error instanceof NotificationValidationError) {
        throw error; // Re-throw validation errors as-is
      }
      
      console.error('Error fetching interested users:', error);
      
      // Handle specific database errors
      if (error.code === '42P01') { // Table doesn't exist
        throw new NotificationServiceError(
          'Project interest table not found. Please contact support.',
          'TABLE_NOT_FOUND',
          'getInterestedUsers',
          { projectId, error: error.message }
        );
      }
      
      if (error.code === '23503') { // Foreign key violation
        throw new NotificationServiceError(
          'Invalid project ID provided',
          'INVALID_PROJECT_REFERENCE',
          'getInterestedUsers',
          { projectId, error: error.message }
        );
      }
      
      throw new NotificationServiceError(
        `Failed to fetch interested users: ${error.message}`,
        'FETCH_USERS_FAILED',
        'getInterestedUsers',
        { projectId, originalError: error.message }
      );
    }
  }

  /**
   * Get users who expressed interest AND have platform notifications enabled
   * Requirements: 1.1, 1.4, 2.1, 3.1, 5.1, 9.1, 9.2
   */
  async getInterestedUsersWithNotificationPreferences(projectId: string): Promise<InterestedUser[]> {
    try {
      // Validate input
      if (!projectId?.trim()) {
        throw new NotificationValidationError(
          'Project ID is required to fetch interested users with notification preferences',
          'PROJECT_ID_REQUIRED',
          'projectId'
        );
      }

      // Check cache first
      const cacheKey = `interested_users_with_prefs_${projectId}`;
      const cachedData = PostReleaseNotificationService.cache.get<InterestedUser[]>(cacheKey);
      if (cachedData) {
        return cachedData;
      }

      // Fetch data with retry mechanism - join with project_notifications
      const data = await NotificationErrorRecovery.retryDatabaseOperation(async () => {
        const { data, error } = await supabase
          .from('project_interest')
          .select(`
            *,
            user:user_id (
              id,
              email,
              username
            ),
            project_notifications!inner (
              platform_notifications,
              email_notifications
            )
          `)
          .eq('project_id', projectId)
          .eq('project_notifications.platform_notifications', true) // Only users who want platform notifications
          .order('created_at', { ascending: false })
          .limit(1000);

        if (error) {
          throw error;
        }

        return data;
      });

      if (!data || data.length === 0) {
        console.log(`No interested users with platform notifications enabled found for project ${projectId}`);
        return [];
      }

      // Transform the data (same logic as getInterestedUsers)
      const validUsers = data
        .filter(item => {
          if (!item.user || !item.user.email) {
            console.warn(`Skipping user ${item.user_id} - missing user data or email`);
            return false;
          }
          return true;
        })
        .map(item => ({
          id: item.id,
          user_id: item.user_id,
          email: item.user.email,
          username: item.user.username || 'Unknown User',
          interest_level: item.interest_level || 1,
          willing_to_pay: item.willing_to_pay || false,
          committed_to_buy: item.committed_to_buy || false,
          early_bird_commitment: item.early_bird_commitment || false,
          max_price_willing: item.max_price_willing || null,
          created_at: item.created_at
        }));

      // Cache the filtered results
      PostReleaseNotificationService.cache.set(cacheKey, validUsers, 5); // 5 minute cache

      console.log(`Found ${validUsers.length} interested users with platform notifications enabled for project ${projectId}`);
      return validUsers;
    } catch (error: any) {
      console.error('Error getting interested users with notification preferences:', error);
      
      // Fallback to all interested users if notification preferences query fails
      console.log('Falling back to all interested users due to notification preferences query failure');
      return await this.getInterestedUsers(projectId);
    }
  }

  /**
   * Generate email template with recipient list and pre-filled content
   * Requirements: 2.1, 2.2, 2.3, 2.4, 2.5, 5.1, 5.2, 6.5, 9.1, 9.2
   */
  async generateEmailTemplate(project: PreReleaseProject, interestedUsers: InterestedUser[], enableTracking: boolean = true): Promise<EmailTemplateData> {
    try {
      // Validate inputs
      NotificationValidation.validateProjectForNotifications(project);
      NotificationValidation.validateInterestedUsers(interestedUsers);

      // Extract recipient emails and user data
      const recipients = interestedUsers.map(user => user.email);
      const recipientData = interestedUsers.map(user => ({
        userId: user.user_id,
        email: user.email
      }));

      // Generate subject line based on project details
      const subject = this.generateSubjectLine(project);

      // Generate project pricing information
      const pricing = this.formatPricingInfo(project);

      // Project details for template
      const projectDetails = {
        title: project.title,
        description: project.description,
        imageUrl: project.image_url,
        demoUrl: project.demo_url,
        pricing
      };

      let htmlContent: string;
      let plainTextContent: string;
      let personalizedTemplates: Map<string, string> | undefined;
      let templateId: string | undefined;

      if (enableTracking && recipientData.length > 0) {
        // Generate tracking-enabled templates
        const baseHtmlContent = this.generateHtmlEmailContent(project, projectDetails, true);
        const basePlainTextContent = this.generatePlainTextEmailContent(project, projectDetails, true);

        // Define trackable links
        const trackableLinks = [];
        if (project.demo_url) {
          trackableLinks.push({
            type: 'demo_link' as const,
            url: project.demo_url,
            placeholder: '{{DEMO_LINK_TRACKING_URL}}'
          });
        }
        
        // Add project page link (assuming it exists)
        trackableLinks.push({
          type: 'project_page' as const,
          url: `/projects/${project.id}`,
          placeholder: '{{PROJECT_PAGE_TRACKING_URL}}'
        });

        // Record template generation first
        templateId = await emailClickTrackingService.recordTemplateGeneration(
          project.id,
          recipients.length,
          subject,
          baseHtmlContent,
          {
            projectTitle: project.title,
            trackingEnabled: true,
            linksCount: trackableLinks.length
          }
        );

        // Generate personalized templates with tracking URLs
        personalizedTemplates = await emailClickTrackingService.createTrackingEnabledTemplate(
          project.id,
          baseHtmlContent,
          recipientData,
          trackableLinks
        );

        // Use base templates for preview
        htmlContent = baseHtmlContent;
        plainTextContent = basePlainTextContent;
      } else {
        // Generate standard templates without tracking
        htmlContent = this.generateHtmlEmailContent(project, projectDetails, false);
        plainTextContent = this.generatePlainTextEmailContent(project, projectDetails, false);
      }

      // Record the email template generation
      await this.recordNotificationCampaign(
        project.id,
        'email_template',
        recipients.length,
        {
          subject,
          recipients: recipients.length,
          projectTitle: project.title,
          templateVersion: '2.0',
          trackingEnabled: enableTracking,
          templateId
        }
      );

      // Track contact history for each user
      for (const user of interestedUsers) {
        await this.trackContactHistory(
          project.id,
          user.user_id,
          'email_template',
          `Email template generated for ${project.title}`,
          {
            subject,
            templateGenerated: new Date().toISOString(),
            recipientCount: recipients.length,
            trackingEnabled: enableTracking,
            templateId
          }
        );
      }

      return {
        recipients,
        subject,
        htmlContent,
        plainTextContent,
        trackingEnabled: enableTracking,
        templateId,
        personalizedTemplates,
        projectDetails
      };
    } catch (error: any) {
      if (error instanceof NotificationValidationError) {
        throw error; // Re-throw validation errors as-is
      }
      
      console.error('Error generating email template:', error);
      
      // Handle specific error types
      if (error.message?.includes('tracking')) {
        throw new NotificationServiceError(
          'Failed to set up email tracking. Template generated without tracking.',
          'TRACKING_SETUP_FAILED',
          'generateEmailTemplate',
          { originalError: error.message, enableTracking }
        );
      }
      
      if (error.message?.includes('database') || error.message?.includes('supabase')) {
        throw new NotificationServiceError(
          'Database error while generating email template. Please try again.',
          'DATABASE_ERROR',
          'generateEmailTemplate',
          { originalError: error.message }
        );
      }
      
      throw new NotificationServiceError(
        `Failed to generate email template: ${error.message}`,
        'TEMPLATE_GENERATION_FAILED',
        'generateEmailTemplate',
        { originalError: error.message }
      );
    }
  }

  /**
   * Generate subject line based on project details
   * Requirements: 2.2
   */
  private generateSubjectLine(project: PreReleaseProject): string {
    const baseSubject = `🎉 ${project.title} is Now Available!`;
    
    // Add early bird discount mention if applicable
    if (project.early_bird_discount && project.early_bird_discount > 0) {
      return `${baseSubject} (Early Bird Discount Available)`;
    }
    
    // Add pricing mention for high-value projects
    if (project.proposed_price && project.proposed_price > 100) {
      return `${baseSubject} - Premium Release`;
    }
    
    return baseSubject;
  }

  /**
   * Format pricing information for display
   * Requirements: 2.4
   */
  private formatPricingInfo(project: PreReleaseProject): string {
    if (!project.proposed_price) {
      return 'Contact for pricing';
    }

    let pricing = `$${project.proposed_price}`;
    
    if (project.early_bird_discount && project.early_bird_discount > 0) {
      const discountedPrice = project.proposed_price - (project.proposed_price * project.early_bird_discount / 100);
      pricing += ` (Early Bird Special: $${discountedPrice.toFixed(2)} - Save ${project.early_bird_discount}%)`;
    }
    
    return pricing;
  }

  /**
   * Generate downloadable email template with proper formatting
   * Requirements: 2.5, 9.1, 9.2
   */
  async generateDownloadableTemplate(project: PreReleaseProject, interestedUsers: InterestedUser[]): Promise<string> {
    try {
      // Validate inputs
      NotificationValidation.validateProjectForNotifications(project);
      NotificationValidation.validateInterestedUsers(interestedUsers);

      const templateData = await this.generateEmailTemplate(project, interestedUsers);
      
      // Create a formatted template for easy copying
      const downloadableContent = `
EMAIL TEMPLATE FOR: ${project.title}
Generated on: ${new Date().toLocaleDateString()}
Recipients: ${templateData.recipients.length} users

=== RECIPIENT LIST ===
${templateData.recipients.join('; ')}

=== SUBJECT LINE ===
${templateData.subject}

=== EMAIL CONTENT (HTML) ===
${templateData.htmlContent}

=== EMAIL CONTENT (PLAIN TEXT) ===
${templateData.plainTextContent}

=== INSTRUCTIONS ===
1. Copy the recipient list above and paste into your email client's BCC field
2. Copy the subject line above
3. Copy either the HTML or plain text content based on your email client's capabilities
4. Send the email from your preferred email account

Note: This template was generated automatically. You may customize the content before sending.
`;

      return downloadableContent;
    } catch (error: any) {
      if (error instanceof NotificationValidationError || error instanceof NotificationServiceError) {
        throw error; // Re-throw custom errors as-is
      }
      
      console.error('Error generating downloadable template:', error);
      
      throw new NotificationServiceError(
        `Failed to generate downloadable template: ${error.message}`,
        'DOWNLOADABLE_TEMPLATE_FAILED',
        'generateDownloadableTemplate',
        { originalError: error.message }
      );
    }
  }

  /**
   * Send website messages to interested users (Refactored for efficiency)
   * Creates ONE message and targets it to specific users via project_release_messages
   * Requirements: 3.1, 3.2, 3.3, 3.4, 3.5, 9.1, 9.2
   */
  async sendWebsiteMessages(project: PreReleaseProject, interestedUsers: InterestedUser[], customMessage?: string): Promise<number> {
    try {
      // Validate inputs
      NotificationValidation.validateProjectForNotifications(project);
      NotificationValidation.validateInterestedUsers(interestedUsers);
      
      if (customMessage) {
        NotificationValidation.validateCustomMessage(customMessage);
      }

      if (interestedUsers.length === 0) {
        console.log(`No interested users found for project ${project.title}`);
        return 0;
      }

      const messageText = customMessage || this.generateWebsiteMessageContent(project);
      
      // Create ONE website message for this project release
      const messageId = await WebsiteMessageService.createMessage({
        message_text: messageText,
        message_type: 'project_release', // Use project_release type to exclude from global feeds
        background_color: '#e8f5e8',
        text_color: '#2e7d32',
        start_date: new Date().toISOString(),
        end_date: null // No end date for project release messages
      });

      console.log(`Created website message ${messageId} for project ${project.title}`);

      // Record delivery to each interested user in parallel for better performance
      const deliveryPromises = interestedUsers.map(async (user) => {
        try {
          // Validate individual user before processing
          NotificationValidation.validateInterestedUser(user);
          
          // Record the message delivery in project_release_messages table
          await this.recordProjectReleaseMessage(project.id, messageId, user.user_id);
          
          // Track contact history for this user
          await this.trackContactHistory(
            project.id,
            user.user_id,
            'website_message',
            messageText,
            {
              messageId,
              sentAt: new Date().toISOString(),
              messageType: 'project_release'
            }
          );
          
          return { success: true, userId: user.user_id };
        } catch (userError: any) {
          console.error(`Failed to record delivery for user ${user.user_id}:`, userError);
          return { success: false, userId: user.user_id, error: userError.message };
        }
      });

      // Wait for all delivery records to complete
      const deliveryResults = await Promise.allSettled(deliveryPromises);
      
      // Count successes and failures
      let successCount = 0;
      const failedUsers: string[] = [];
      
      deliveryResults.forEach((result, index) => {
        if (result.status === 'fulfilled' && result.value.success) {
          successCount++;
        } else {
          const user = interestedUsers[index];
          failedUsers.push(user.username || user.user_id);
        }
      });

      // Record the notification campaign
      await this.recordNotificationCampaign(
        project.id,
        'website_message',
        successCount,
        undefined,
        messageText
      );

      if (failedUsers.length > 0) {
        console.warn(`Failed to record delivery for ${failedUsers.length} users:`, failedUsers);
      }

      console.log(`Successfully sent project release message to ${successCount}/${interestedUsers.length} interested users`);
      return successCount;
    } catch (error: any) {
      if (error instanceof NotificationValidationError) {
        throw error; // Re-throw validation errors as-is
      }
      
      console.error('Error sending website messages:', error);
      
      // Handle specific error types
      if (error.message?.includes('database') || error.message?.includes('supabase')) {
        throw new NotificationServiceError(
          'Database error while sending website messages. Some messages may not have been sent.',
          'DATABASE_ERROR',
          'sendWebsiteMessages',
          { originalError: error.message }
        );
      }
      
      if (error.message?.includes('WebsiteMessageService')) {
        throw new NotificationServiceError(
          'Website message service error. Please check the message service configuration.',
          'MESSAGE_SERVICE_ERROR',
          'sendWebsiteMessages',
          { originalError: error.message }
        );
      }
      
      throw new NotificationServiceError(
        `Failed to send website messages: ${error.message}`,
        'MESSAGE_SENDING_FAILED',
        'sendWebsiteMessages',
        { originalError: error.message }
      );
    }
  }

  /**
   * Generate website message templates for different scenarios
   * Requirements: 3.1, 3.2
   */
  generateWebsiteMessageTemplates(project: PreReleaseProject): { [key: string]: string } {
    const templates = {
      standard: this.generateWebsiteMessageContent(project),
      
      withDiscount: project.early_bird_discount 
        ? `🎉 ${project.title} is now available with ${project.early_bird_discount}% early bird discount! The project you showed interest in has been officially released. ${project.demo_url ? `Check it out: ${project.demo_url}` : 'Visit the project page for more details.'}`
        : this.generateWebsiteMessageContent(project),
      
      premium: `✨ Premium Release: ${project.title} is now available! Thank you for your interest during the pre-release phase. Your feedback helped shape this project. ${project.demo_url ? `Experience it now: ${project.demo_url}` : 'Visit the project page to learn more.'}`,
      
      personal: `Hi! Remember ${project.title}? It's finally here! 🎉 The project you showed interest in has been officially released and is ready for you to explore. ${project.demo_url ? `Take a look: ${project.demo_url}` : 'Check out the project page for all the details.'}`,
      
      urgent: `⏰ Limited Time: ${project.title} is now live! Don't miss out on the project you were waiting for. ${project.early_bird_discount ? `Early bird pricing available for a limited time.` : ''} ${project.demo_url ? `Get started: ${project.demo_url}` : 'Visit the project page now.'}`
    };

    return templates;
  }

  /**
   * Send bulk website messages with delivery tracking
   * Requirements: 3.3, 3.4, 3.5, 9.1, 9.2
   */
  async sendBulkWebsiteMessages(
    project: PreReleaseProject, 
    interestedUsers: InterestedUser[], 
    messageTemplate: string,
    messageType: 'project_release' | 'discount_offer' | 'update' = 'project_release'
  ): Promise<{ successCount: number; failedUsers: string[]; messageIds: string[] }> {
    try {
      // Validate inputs
      NotificationValidation.validateProjectForNotifications(project);
      NotificationValidation.validateInterestedUsers(interestedUsers);
      NotificationValidation.validateCustomMessage(messageTemplate);
      
      // Validate bulk operation parameters
      NotificationValidation.validateBulkOperationParams(interestedUsers.length, 100); // max 100 per batch
      
      let successCount = 0;
      const failedUsers: string[] = [];
      const messageIds: string[] = [];

      // Process users in batches to avoid overwhelming the system
      const batchSize = 10;
      for (let i = 0; i < interestedUsers.length; i += batchSize) {
        const batch = interestedUsers.slice(i, i + batchSize);
        
        const batchPromises = batch.map(async (user) => {
          try {
            // Validate individual user
            NotificationValidation.validateInterestedUser(user, i + batch.indexOf(user));
            
            // Create personalized message
            const personalizedMessage = this.personalizeMessage(messageTemplate, user, project);
            
            // Create website message
            const messageId = await WebsiteMessageService.createMessage({
              message_text: personalizedMessage,
              message_type: 'project_release', // Use project_release type to exclude from global feeds
              background_color: this.getMessageBackgroundColor(messageType),
              text_color: this.getMessageTextColor(messageType),
              start_date: new Date().toISOString(),
              end_date: null
            });

            // Record the message delivery
            await this.recordProjectReleaseMessage(project.id, messageId, user.user_id);
            
            // Track contact history
            const contactType = messageType === 'project_release' || messageType === 'update' 
              ? 'website_message' 
              : messageType as 'email_template' | 'website_message' | 'discount_offer';
              
            await this.trackContactHistory(
              project.id,
              user.user_id,
              contactType,
              personalizedMessage,
              {
                messageId,
                messageType,
                sentAt: new Date().toISOString(),
                batchNumber: Math.floor(i / batchSize) + 1
              }
            );

            return { success: true, messageId, userId: user.user_id };
          } catch (error: any) {
            console.error(`Failed to send message to user ${user.user_id}:`, error);
            return { success: false, error: error.message, userId: user.user_id, username: user.username };
          }
        });

        const batchResults = await Promise.all(batchPromises);
        
        // Process batch results
        batchResults.forEach(result => {
          if (result.success) {
            successCount++;
            if (result.messageId) {
              messageIds.push(result.messageId);
            }
          } else {
            failedUsers.push(result.username || result.userId);
          }
        });

        // Small delay between batches to prevent rate limiting
        if (i + batchSize < interestedUsers.length) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      // Record the bulk notification campaign
      await this.recordNotificationCampaign(
        project.id,
        'website_message',
        successCount,
        {
          messageTemplate,
          messageType,
          batchSize,
          totalBatches: Math.ceil(interestedUsers.length / batchSize)
        },
        messageTemplate
      );

      return { successCount, failedUsers, messageIds };
    } catch (error: any) {
      if (error instanceof NotificationValidationError) {
        throw error; // Re-throw validation errors as-is
      }
      
      console.error('Error sending bulk website messages:', error);
      
      // Handle specific error types
      if (error.message?.includes('batch') || error.message?.includes('rate limit')) {
        throw new NotificationServiceError(
          'Rate limiting or batch processing error. Some messages may not have been sent.',
          'RATE_LIMIT_ERROR',
          'sendBulkWebsiteMessages',
          { originalError: error.message }
        );
      }
      
      if (error.message?.includes('database') || error.message?.includes('supabase')) {
        throw new NotificationServiceError(
          'Database error during bulk message sending. Please retry the operation.',
          'DATABASE_ERROR',
          'sendBulkWebsiteMessages',
          { originalError: error.message }
        );
      }
      
      throw new NotificationServiceError(
        `Failed to send bulk website messages: ${error.message}`,
        'BULK_SENDING_FAILED',
        'sendBulkWebsiteMessages',
        { originalError: error.message }
      );
    }
  }

  /**
   * Personalize message content for individual users
   */
  private personalizeMessage(template: string, user: InterestedUser, project: PreReleaseProject): string {
    return template
      .replace(/\{username\}/g, user.username)
      .replace(/\{projectTitle\}/g, project.title)
      .replace(/\{interestLevel\}/g, user.interest_level.toString());
  }

  /**
   * Get background color based on message type
   */
  private getMessageBackgroundColor(messageType: string): string {
    const colors = {
      'project_release': '#e8f5e8',
      'discount_offer': '#fff3e0',
      'update': '#e3f2fd'
    };
    return colors[messageType as keyof typeof colors] || '#f5f5f5';
  }

  /**
   * Get text color based on message type
   */
  private getMessageTextColor(messageType: string): string {
    const colors = {
      'project_release': '#2e7d32',
      'discount_offer': '#f57c00',
      'update': '#1976d2'
    };
    return colors[messageType as keyof typeof colors] || '#333333';
  }

  /**
   * Track contact history for analytics and future campaigns
   * Requirements: 1.1, 1.4, 2.1, 3.1, 9.1, 9.2
   */
  async trackContactHistory(
    projectId: string, 
    userId: string, 
    contactType: 'email_template' | 'website_message' | 'discount_offer',
    messageContent?: string,
    metadata?: any
  ): Promise<void> {
    try {
      // Validate required parameters
      if (!projectId?.trim()) {
        throw new NotificationValidationError(
          'Project ID is required for contact tracking',
          'PROJECT_ID_REQUIRED',
          'projectId'
        );
      }
      
      if (!userId?.trim()) {
        throw new NotificationValidationError(
          'User ID is required for contact tracking',
          'USER_ID_REQUIRED',
          'userId'
        );
      }
      
      if (!contactType || !['email_template', 'website_message', 'discount_offer'].includes(contactType)) {
        throw new NotificationValidationError(
          'Valid contact type is required (email_template, website_message, or discount_offer)',
          'INVALID_CONTACT_TYPE',
          'contactType'
        );
      }

      // Use retry mechanism for database operations
      const result = await NotificationErrorRecovery.retryDatabaseOperation(async () => {
        const { error } = await supabase
          .from('user_contact_history')
          .insert([{
            project_id: projectId,
            user_id: userId,
            contact_type: contactType,
            message_content: messageContent,
            metadata: metadata || {}
          }]);

        if (error) {
          throw error;
        }
      });

    } catch (error: any) {
      if (error instanceof NotificationValidationError) {
        throw error; // Re-throw validation errors as-is
      }
      
      console.error('Error tracking contact history:', error);
      
      // Handle specific database errors
      if (error.code === '23503') { // Foreign key violation
        throw new NotificationServiceError(
          'Invalid project or user ID provided',
          'INVALID_REFERENCE',
          'trackContactHistory',
          { projectId, userId, contactType, error: error.message }
        );
      }
      
      if (error.code === '23505') { // Unique constraint violation
        console.warn('Contact history entry already exists, skipping...');
        return; // Don't fail for duplicate entries
      }
      
      throw new NotificationServiceError(
        `Failed to track contact history: ${error.message}`,
        'CONTACT_TRACKING_FAILED',
        'trackContactHistory',
        { projectId, userId, contactType, originalError: error.message }
      );
    }
  }

  /**
   * Record notification campaign for project owner tracking
   * Requirements: 9.1, 9.2
   */
  async recordNotificationCampaign(
    projectId: string,
    notificationType: 'email_template' | 'website_message',
    recipientCount: number,
    templateData?: any,
    messageContent?: string
  ): Promise<string> {
    try {
      // Validate inputs
      if (!projectId?.trim()) {
        throw new NotificationValidationError(
          'Project ID is required for recording campaigns',
          'PROJECT_ID_REQUIRED',
          'projectId'
        );
      }
      
      if (!notificationType || !['email_template', 'website_message'].includes(notificationType)) {
        throw new NotificationValidationError(
          'Valid notification type is required (email_template or website_message)',
          'INVALID_NOTIFICATION_TYPE',
          'notificationType'
        );
      }
      
      if (typeof recipientCount !== 'number' || recipientCount < 0) {
        throw new NotificationValidationError(
          'Recipient count must be a non-negative number',
          'INVALID_RECIPIENT_COUNT',
          'recipientCount'
        );
      }

      // Get authenticated user with retry mechanism
      const { data: { user } } = await NotificationErrorRecovery.retryDatabaseOperation(async () => {
        return await supabase.auth.getUser();
      });
      
      if (!user) {
        throw new NotificationServiceError(
          'User must be authenticated to record notifications',
          'AUTHENTICATION_REQUIRED',
          'recordNotificationCampaign'
        );
      }

      // Record the campaign with retry mechanism using the existing database function
      const result = await NotificationErrorRecovery.retryDatabaseOperation(async () => {
        const { data, error } = await supabase.rpc('insert_project_notification', {
          p_project_id: projectId,
          p_notification_type: notificationType,
          p_recipient_count: recipientCount,
          p_template_data: templateData,
          p_message_content: messageContent
        });

        if (error) {
          throw error;
        }

        return { id: data };
      });

      return result.id;
    } catch (error: any) {
      if (error instanceof NotificationValidationError || error instanceof NotificationServiceError) {
        throw error; // Re-throw custom errors as-is
      }
      
      console.error('Error recording notification campaign:', error);
      
      // Handle specific database errors
      if (error.code === '23503') { // Foreign key violation
        throw new NotificationServiceError(
          'Invalid project ID provided',
          'INVALID_PROJECT_REFERENCE',
          'recordNotificationCampaign',
          { projectId, error: error.message }
        );
      }
      
      throw new NotificationServiceError(
        `Failed to record notification campaign: ${error.message}`,
        'CAMPAIGN_RECORDING_FAILED',
        'recordNotificationCampaign',
        { projectId, notificationType, recipientCount, originalError: error.message }
      );
    }
  }

  /**
   * Record project release message delivery
   * Requirements: 9.1, 9.2
   */
  private async recordProjectReleaseMessage(projectId: string, messageId: string, recipientUserId: string): Promise<void> {
    try {
      // Validate inputs
      if (!projectId?.trim()) {
        throw new NotificationValidationError(
          'Project ID is required for message recording',
          'PROJECT_ID_REQUIRED',
          'projectId'
        );
      }
      
      if (!messageId?.trim()) {
        throw new NotificationValidationError(
          'Message ID is required for message recording',
          'MESSAGE_ID_REQUIRED',
          'messageId'
        );
      }
      
      if (!recipientUserId?.trim()) {
        throw new NotificationValidationError(
          'Recipient user ID is required for message recording',
          'RECIPIENT_USER_ID_REQUIRED',
          'recipientUserId'
        );
      }

      // Record with retry mechanism
      await NotificationErrorRecovery.retryDatabaseOperation(async () => {
        const { error } = await supabase
          .from('project_release_messages')
          .insert([{
            project_id: projectId,
            message_id: messageId,
            recipient_user_id: recipientUserId
          }]);

        if (error) {
          throw error;
        }
      });

    } catch (error: any) {
      if (error instanceof NotificationValidationError) {
        throw error; // Re-throw validation errors as-is
      }
      
      console.error('Error recording project release message:', error);
      
      // Handle specific database errors
      if (error.code === '23503') { // Foreign key violation
        throw new NotificationServiceError(
          'Invalid project, message, or user ID provided',
          'INVALID_REFERENCE',
          'recordProjectReleaseMessage',
          { projectId, messageId, recipientUserId, error: error.message }
        );
      }
      
      if (error.code === '23505') { // Unique constraint violation
        console.warn('Project release message record already exists, skipping...');
        return; // Don't fail for duplicate entries
      }
      
      throw new NotificationServiceError(
        `Failed to record project release message: ${error.message}`,
        'MESSAGE_RECORDING_FAILED',
        'recordProjectReleaseMessage',
        { projectId, messageId, recipientUserId, originalError: error.message }
      );
    }
  }

  /**
   * Generate HTML email content with optional tracking
   * Requirements: 2.4, 5.1, 5.2, 6.5
   */
  private generateHtmlEmailContent(project: PreReleaseProject, projectDetails: any, enableTracking: boolean = false): string {
    const demoUrl = enableTracking && project.demo_url ? '{{DEMO_LINK_TRACKING_URL}}' : project.demo_url;
    const projectPageUrl = enableTracking ? '{{PROJECT_PAGE_TRACKING_URL}}' : `/projects/${project.id}`;
    
    return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${project.title} is Now Available!</title>
    <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px; }
        .header { background: #2e7d32; color: white; padding: 20px; text-align: center; border-radius: 8px 8px 0 0; }
        .content { background: #f9f9f9; padding: 30px; border-radius: 0 0 8px 8px; }
        .project-image { max-width: 100%; height: auto; border-radius: 8px; margin: 20px 0; }
        .cta-button { display: inline-block; background: #2e7d32; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; margin: 20px 0; }
        .secondary-button { display: inline-block; background: #1976d2; color: white; padding: 10px 20px; text-decoration: none; border-radius: 6px; margin: 10px 5px; }
        .footer { text-align: center; margin-top: 30px; color: #666; font-size: 14px; }
        .tracking-info { font-size: 12px; color: #999; margin-top: 20px; }
    </style>
</head>
<body>
    <div class="header">
        <h1>🎉 Great News!</h1>
        <h2>${project.title} is Now Available!</h2>
    </div>
    
    <div class="content">
        <p>Hi there!</p>
        
        <p>We're excited to let you know that <strong>${project.title}</strong>, the project you showed interest in, is now officially released and available!</p>
        
        ${project.image_url ? `<img src="${project.image_url}" alt="${project.title}" class="project-image">` : ''}
        
        <h3>About ${project.title}:</h3>
        <p>${project.description}</p>
        
        <h3>Pricing:</h3>
        <p><strong>${projectDetails.pricing}</strong></p>
        
        <div style="text-align: center; margin: 30px 0;">
          ${demoUrl ? `<a href="${demoUrl}" class="cta-button">View Demo</a>` : ''}
          <a href="${projectPageUrl}" class="secondary-button">View Project Details</a>
        </div>
        
        <p>Thank you for your interest and support during the pre-release phase. Your feedback helped make this project better!</p>
        
        <p>Best regards,<br>The ${project.title} Team</p>
        
        ${enableTracking ? `
        <div class="tracking-info">
          <p><em>This email includes tracking to help us improve our communications. Click tracking helps us understand which content is most valuable to you.</em></p>
        </div>
        ` : ''}
    </div>
    
    <div class="footer">
        <p>You received this email because you expressed interest in ${project.title} during its pre-release phase.</p>
        ${enableTracking ? '<p style="font-size: 11px; color: #999;">Email tracking enabled for analytics purposes.</p>' : ''}
    </div>
</body>
</html>`;
  }

  /**
   * Generate plain text email content with optional tracking
   * Requirements: 2.4, 5.1, 5.2, 6.5
   */
  private generatePlainTextEmailContent(project: PreReleaseProject, projectDetails: any, enableTracking: boolean = false): string {
    const demoUrl = enableTracking && project.demo_url ? '{{DEMO_LINK_TRACKING_URL}}' : project.demo_url;
    const projectPageUrl = enableTracking ? '{{PROJECT_PAGE_TRACKING_URL}}' : `/projects/${project.id}`;
    
    return `🎉 Great News! ${project.title} is Now Available!

Hi there!

We're excited to let you know that ${project.title}, the project you showed interest in, is now officially released and available!

About ${project.title}:
${project.description}

Pricing: ${projectDetails.pricing}

${demoUrl ? `View Demo: ${demoUrl}` : ''}

View Project Details: ${projectPageUrl}

Thank you for your interest and support during the pre-release phase. Your feedback helped make this project better!

Best regards,
The ${project.title} Team

---
You received this email because you expressed interest in ${project.title} during its pre-release phase.
${enableTracking ? '\nThis email includes tracking to help us improve our communications.' : ''}`;
  }

  /**
   * Generate website message content
   */
  private generateWebsiteMessageContent(project: PreReleaseProject): string {
    return `🎉 ${project.title} is now available! The project you showed interest in has been officially released. ${project.demo_url ? `Check it out: ${project.demo_url}` : 'Visit the project page for more details.'}`;
  }

  /**
   * Get message delivery statistics for a project
   * Requirements: 3.4, 3.5, 9.1, 9.2
   */
  async getMessageDeliveryStats(projectId: string): Promise<{
    totalSent: number;
    totalRead: number;
    totalClicked: number;
    totalDismissed: number;
    deliveryRate: number;
    readRate: number;
    clickRate: number;
  }> {
    try {
      // Validate input
      if (!projectId?.trim()) {
        throw new NotificationValidationError(
          'Project ID is required for delivery stats',
          'PROJECT_ID_REQUIRED',
          'projectId'
        );
      }

      // Fetch stats with retry mechanism
      const data = await NotificationErrorRecovery.retryDatabaseOperation(async () => {
        const { data, error } = await supabase
          .from('project_release_messages')
          .select('id, project_id, message_id, recipient_user_id, read_at, clicked_at, dismissed_at')
          .eq('project_id', projectId);

        if (error) {
          throw error;
        }

        return data;
      });

      const totalSent = data?.length || 0;
      const totalRead = data?.filter(msg => msg.read_at).length || 0;
      const totalClicked = data?.filter(msg => msg.clicked_at).length || 0;
      const totalDismissed = data?.filter(msg => msg.dismissed_at).length || 0;

      return {
        totalSent,
        totalRead,
        totalClicked,
        totalDismissed,
        deliveryRate: totalSent > 0 ? 100 : 0, // Assuming all sent messages are delivered
        readRate: totalSent > 0 ? Math.round((totalRead / totalSent) * 100 * 100) / 100 : 0,
        clickRate: totalSent > 0 ? Math.round((totalClicked / totalSent) * 100 * 100) / 100 : 0
      };
    } catch (error: any) {
      if (error instanceof NotificationValidationError) {
        throw error; // Re-throw validation errors as-is
      }
      
      console.error('Error fetching message delivery stats:', error);
      
      // Handle specific database errors
      if (error.code === '42P01') { // Table doesn't exist
        throw new NotificationServiceError(
          'Message tracking table not found. Please contact support.',
          'TABLE_NOT_FOUND',
          'getMessageDeliveryStats',
          { projectId, error: error.message }
        );
      }
      
      if (error.code === '42501') { // Permission denied
        throw new NotificationServiceError(
          'Permission denied while accessing message delivery statistics. Please check your access permissions.',
          'PERMISSION_DENIED',
          'getMessageDeliveryStats',
          { projectId, error: error.message, code: error.code }
        );
      }
      
      throw new NotificationServiceError(
        `Failed to fetch delivery stats: ${error.message}`,
        'STATS_FETCH_FAILED',
        'getMessageDeliveryStats',
        { projectId, originalError: error.message }
      );
    }
  }

  /**
   * Update message engagement tracking
   * Requirements: 3.5, 9.1, 9.2
   */
  async updateMessageEngagement(
    messageId: string, 
    engagementType: 'read' | 'clicked' | 'dismissed'
  ): Promise<void> {
    try {
      // Validate inputs
      if (!messageId?.trim()) {
        throw new NotificationValidationError(
          'Message ID is required for engagement tracking',
          'MESSAGE_ID_REQUIRED',
          'messageId'
        );
      }
      
      if (!engagementType || !['read', 'clicked', 'dismissed'].includes(engagementType)) {
        throw new NotificationValidationError(
          'Valid engagement type is required (read, clicked, or dismissed)',
          'INVALID_ENGAGEMENT_TYPE',
          'engagementType'
        );
      }

      const updateData: any = {};
      const timestamp = new Date().toISOString();

      switch (engagementType) {
        case 'read':
          updateData.read_at = timestamp;
          break;
        case 'clicked':
          updateData.clicked_at = timestamp;
          break;
        case 'dismissed':
          updateData.dismissed_at = timestamp;
          break;
      }

      // Update with retry mechanism
      await NotificationErrorRecovery.retryDatabaseOperation(async () => {
        const { error } = await supabase
          .from('project_release_messages')
          .update(updateData)
          .eq('message_id', messageId);

        if (error) {
          throw error;
        }
      });

    } catch (error: any) {
      if (error instanceof NotificationValidationError) {
        throw error; // Re-throw validation errors as-is
      }
      
      console.error('Error updating message engagement:', error);
      
      // Handle specific database errors
      if (error.code === '23503') { // Foreign key violation
        throw new NotificationServiceError(
          'Invalid message ID provided',
          'INVALID_MESSAGE_REFERENCE',
          'updateMessageEngagement',
          { messageId, engagementType, error: error.message }
        );
      }
      
      throw new NotificationServiceError(
        `Failed to update message engagement: ${error.message}`,
        'ENGAGEMENT_UPDATE_FAILED',
        'updateMessageEngagement',
        { messageId, engagementType, originalError: error.message }
      );
    }
  }

  /**
   * Get email engagement analytics for a project
   * Requirements: 5.1, 5.2, 5.3, 9.1, 9.2
   */
  async getEmailEngagementAnalytics(projectId: string): Promise<any> {
    try {
      // Validate input
      if (!projectId?.trim()) {
        throw new NotificationValidationError(
          'Project ID is required for email analytics',
          'PROJECT_ID_REQUIRED',
          'projectId'
        );
      }

      // Get analytics with retry mechanism
      return await NotificationErrorRecovery.retryDatabaseOperation(async () => {
        return await emailClickTrackingService.getEmailEngagementAnalytics(projectId);
      });
    } catch (error: any) {
      if (error instanceof NotificationValidationError) {
        throw error; // Re-throw validation errors as-is
      }
      
      console.error('Error getting email engagement analytics:', error);
      
      // Handle specific service errors
      if (error.message?.includes('emailClickTrackingService')) {
        throw new NotificationServiceError(
          'Email tracking service error. Please check the tracking service configuration.',
          'EMAIL_TRACKING_SERVICE_ERROR',
          'getEmailEngagementAnalytics',
          { projectId, originalError: error.message }
        );
      }
      
      throw new NotificationServiceError(
        `Failed to get email engagement analytics: ${error.message}`,
        'EMAIL_ANALYTICS_FAILED',
        'getEmailEngagementAnalytics',
        { projectId, originalError: error.message }
      );
    }
  }

  /**
   * Get comprehensive engagement analytics for both email and website messages
   * Requirements: 5.1, 5.2, 5.3, 9.1, 9.2
   */
  async getComprehensiveEngagementAnalytics(projectId: string): Promise<{
    email: any;
    websiteMessages: any;
    combined: {
      totalContacts: number;
      totalEngagements: number;
      overallEngagementRate: number;
    };
  }> {
    try {
      // Validate input
      if (!projectId?.trim()) {
        throw new NotificationValidationError(
          'Project ID is required for comprehensive analytics',
          'PROJECT_ID_REQUIRED',
          'projectId'
        );
      }

      // Get email analytics
      const emailAnalytics = await this.getEmailEngagementAnalytics(projectId);
      
      // Get website message analytics
      const websiteMessageAnalytics = await this.getMessageDeliveryStats(projectId);
      
      // Calculate combined metrics with error handling
      let totalContacts = 0;
      let totalEngagements = 0;
      let overallEngagementRate = 0;
      
      try {
        const emailContacts = emailAnalytics?.total_recipients || 0;
        const emailEngagements = emailAnalytics?.unique_clickers || 0;
        const websiteContacts = websiteMessageAnalytics?.totalSent || 0;
        const websiteEngagements = websiteMessageAnalytics?.totalClicked || 0;
        
        totalContacts = emailContacts + websiteContacts;
        totalEngagements = emailEngagements + websiteEngagements;
        overallEngagementRate = totalContacts > 0 ? (totalEngagements / totalContacts) * 100 : 0;
      } catch (calculationError: any) {
        console.warn('Error calculating combined metrics, using defaults:', calculationError);
        // Continue with default values (0)
      }

      return {
        email: emailAnalytics,
        websiteMessages: websiteMessageAnalytics,
        combined: {
          totalContacts,
          totalEngagements,
          overallEngagementRate: Math.round(overallEngagementRate * 100) / 100
        }
      };
    } catch (error: any) {
      if (error instanceof NotificationValidationError || error instanceof NotificationServiceError) {
        throw error; // Re-throw custom errors as-is
      }
      
      console.error('Error getting comprehensive engagement analytics:', error);
      
      throw new NotificationServiceError(
        `Failed to get comprehensive engagement analytics: ${error.message}`,
        'COMPREHENSIVE_ANALYTICS_FAILED',
        'getComprehensiveEngagementAnalytics',
        { projectId, originalError: error.message }
      );
    }
  }

  /**
   * Get project analytics - wrapper method for comprehensive analytics
   * Requirements: 4.1, 4.2, 4.3, 5.1, 5.2, 5.3
   */
  async getProjectAnalytics(projectId: string): Promise<any> {
    try {
      // Validate input
      if (!projectId?.trim()) {
        throw new NotificationValidationError(
          'Project ID is required for project analytics',
          'PROJECT_ID_REQUIRED',
          'projectId'
        );
      }

      // Get comprehensive analytics data
      const comprehensiveAnalytics = await this.getComprehensiveEngagementAnalytics(projectId);
      
      // Get notification campaign records
      const campaignData = await NotificationErrorRecovery.retryDatabaseOperation(async () => {
        const { data, error } = await supabase
          .from('project_release_notifications')
          .select('id, project_id, notification_type, recipient_count, template_data, message_content, created_at')
          .eq('project_id', projectId)
          .order('created_at', { ascending: false });

        if (error) {
          throw error;
        }

        return data || [];
      });

      // Get contact history summary
      const contactHistoryData = await NotificationErrorRecovery.retryDatabaseOperation(async () => {
        const { data, error } = await supabase
          .from('user_contact_history')
          .select('contact_type, contact_date, metadata')
          .eq('project_id', projectId)
          .order('contact_date', { ascending: false });

        if (error) {
          throw error;
        }

        return data || [];
      });

      // Process campaign statistics
      const emailCampaigns = campaignData.filter(c => c.notification_type === 'email_template');
      const websiteCampaigns = campaignData.filter(c => c.notification_type === 'website_message');
      
      const totalEmailRecipients = emailCampaigns.reduce((sum, c) => sum + (c.recipient_count || 0), 0);
      const totalWebsiteRecipients = websiteCampaigns.reduce((sum, c) => sum + (c.recipient_count || 0), 0);

      // Process contact history by type
      const contactsByType = contactHistoryData.reduce((acc, contact) => {
        acc[contact.contact_type] = (acc[contact.contact_type] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      // Calculate time-based analytics
      const last30Days = new Date();
      last30Days.setDate(last30Days.getDate() - 30);
      
      const recentContacts = contactHistoryData.filter(
        contact => new Date(contact.contact_date) >= last30Days
      );

      return {
        summary: {
          totalCampaigns: campaignData.length,
          totalContacts: comprehensiveAnalytics.combined.totalContacts,
          totalEngagements: comprehensiveAnalytics.combined.totalEngagements,
          overallEngagementRate: comprehensiveAnalytics.combined.overallEngagementRate
        },
        campaigns: {
          email: {
            count: emailCampaigns.length,
            totalRecipients: totalEmailRecipients,
            analytics: comprehensiveAnalytics.email
          },
          website: {
            count: websiteCampaigns.length,
            totalRecipients: totalWebsiteRecipients,
            analytics: comprehensiveAnalytics.websiteMessages
          }
        },
        contactHistory: {
          total: contactHistoryData.length,
          byType: contactsByType,
          recent: recentContacts.length,
          last30Days: recentContacts.length
        },
        timeline: {
          campaigns: campaignData.map(campaign => ({
            id: campaign.id,
            type: campaign.notification_type,
            recipients: campaign.recipient_count,
            date: campaign.created_at
          })),
          contacts: contactHistoryData.slice(0, 50).map(contact => ({
            type: contact.contact_type,
            date: contact.contact_date
          }))
        }
      };
    } catch (error: any) {
      if (error instanceof NotificationValidationError || error instanceof NotificationServiceError) {
        throw error; // Re-throw custom errors as-is
      }
      
      console.error('Error getting project analytics:', error);
      
      // Handle specific database permission errors
      if (error.code === '42501') { // Permission denied
        throw new NotificationServiceError(
          'Permission denied while accessing project analytics data. Please check your access permissions.',
          'PERMISSION_DENIED',
          'getProjectAnalytics',
          { projectId, error: error.message, code: error.code }
        );
      }
      
      if (error.code === '23503') { // Foreign key violation
        throw new NotificationServiceError(
          'Invalid project ID provided for analytics',
          'INVALID_PROJECT_REFERENCE',
          'getProjectAnalytics',
          { projectId, error: error.message }
        );
      }
      
      throw new NotificationServiceError(
        `Failed to get project analytics: ${error.message}`,
        'PROJECT_ANALYTICS_FAILED',
        'getProjectAnalytics',
        { projectId, originalError: error.message }
      );
    }
  }

  /**
   * Get contact history for a project without using user table relationships
   * This avoids RLS permission issues by fetching user data separately
   * Requirements: 5.1, 5.2
   */
  async getContactHistory(projectId: string): Promise<ContactHistoryWithUser[]> {
    try {
      // Validate input
      if (!projectId?.trim()) {
        throw new NotificationValidationError(
          'Project ID is required for contact history',
          'PROJECT_ID_REQUIRED',
          'projectId'
        );
      }

      // Get contact history without user relationship
      const contactHistoryData = await NotificationErrorRecovery.retryDatabaseOperation(async () => {
        const { data, error } = await supabase
          .from('user_contact_history')
          .select('id, user_id, project_id, contact_type, contact_date, metadata, engagement_tracked, clicked_through, converted_to_purchase')
          .eq('project_id', projectId)
          .order('contact_date', { ascending: false });

        if (error) {
          // Handle permission denied errors gracefully
          if (error.code === '42501') {
            console.warn('Permission denied for user_contact_history table - using fallback data');
            return [];
          }
          throw error;
        }

        return data || [];
      });

      // Get unique user IDs from contact history
      const userIds = [...new Set(contactHistoryData.map(contact => contact.user_id))];
      
      // Fetch user data separately to avoid RLS issues with relationships
      const userData = await NotificationErrorRecovery.retryDatabaseOperation(async () => {
        if (userIds.length === 0) return [];
        
        const { data, error } = await supabase
          .from('users')
          .select('id, username, email')
          .in('id', userIds);

        if (error) {
          // Handle permission denied errors gracefully
          if (error.code === '42501') {
            console.warn('Permission denied for users table - contact history will show without user details');
            return [];
          }
          throw error;
        }

        return data || [];
      });

      // Create a map of user data for quick lookup
      const userMap = userData.reduce((acc, user) => {
        acc[user.id] = user;
        return acc;
      }, {} as Record<string, any>);

      // Combine contact history with user data
      const contactHistoryWithUsers: ContactHistoryWithUser[] = contactHistoryData.map(contact => ({
        id: contact.id,
        project_id: contact.project_id,
        user_id: contact.user_id,
        contact_type: contact.contact_type,
        contact_date: contact.contact_date,
        engagement_tracked: contact.engagement_tracked || false,
        clicked_through: contact.clicked_through || false,
        converted_to_purchase: contact.converted_to_purchase || false,
        metadata: contact.metadata,
        user: userMap[contact.user_id] || { 
          id: contact.user_id,
          username: 'Unknown User', 
          email: 'N/A' 
        }
      }));

      return contactHistoryWithUsers;

    } catch (error: any) {
      console.error('Error in getContactHistory:', error);
      throw new NotificationServiceError(
        `Failed to fetch contact history: ${error.message}`,
        'CONTACT_HISTORY_FETCH_ERROR',
        'getContactHistory',
        { projectId, originalError: error.message }
      );
    }
  }

  /**
   * Process items in batches to improve performance for large datasets
   * Requirements: 5.1, 5.2
   */
  private async processBatch<T, R>(
    items: T[],
    batchSize: number,
    processor: (batch: T[]) => Promise<R[]>
  ): Promise<R[]> {
    const results: R[] = [];
    
    for (let i = 0; i < items.length; i += batchSize) {
      const batch = items.slice(i, i + batchSize);
      try {
        const batchResults = await processor(batch);
        results.push(...batchResults);
      } catch (error) {
        console.error(`Batch processing failed for items ${i}-${i + batch.length - 1}:`, error);
        // Continue with next batch instead of failing completely
      }
    }
    
    return results;
  }

  /**
   * Send website messages to interested users with batch processing (Refactored for efficiency)
   * Creates ONE message and targets it to specific users via project_release_messages
   * Requirements: 3.1, 3.2, 3.3, 3.4, 3.5, 5.1, 5.2, 9.1, 9.2
   */
  async sendWebsiteMessagesBatched(project: PreReleaseProject, interestedUsers: InterestedUser[], customMessage?: string): Promise<number> {
    try {
      // Validate inputs
      NotificationValidation.validateProjectForNotifications(project);
      NotificationValidation.validateInterestedUsers(interestedUsers);
      
      if (customMessage) {
        NotificationValidation.validateCustomMessage(customMessage);
      }

      if (interestedUsers.length === 0) {
        console.log(`No interested users found for project ${project.title}`);
        return 0;
      }

      const messageText = customMessage || this.generateWebsiteMessageContent(project);
      
      // Create ONE website message for this project release
      const messageId = await WebsiteMessageService.createMessage({
        message_text: messageText,
        message_type: 'project_release', // Use project_release type to exclude from global feeds
        background_color: '#e8f5e8',
        text_color: '#2e7d32',
        start_date: new Date().toISOString(),
        end_date: null // No end date for project release messages
      });

      console.log(`Created website message ${messageId} for project ${project.title} (batched processing)`);

      const BATCH_SIZE = 50; // Process users in batches of 50
      
      // Process users in batches for better performance
      const results = await this.processBatch(
        interestedUsers,
        BATCH_SIZE,
        async (userBatch: InterestedUser[]) => {
          const batchPromises = userBatch.map(async (user) => {
            try {
              // Validate individual user before processing
              NotificationValidation.validateInterestedUser(user);
              
              // Record the message delivery in project_release_messages table
              await this.recordProjectReleaseMessage(project.id, messageId, user.user_id);
              
              // Track contact history for this user
              await this.trackContactHistory(
                project.id,
                user.user_id,
                'website_message',
                messageText,
                {
                  messageId,
                  sentAt: new Date().toISOString(),
                  messageType: 'project_release'
                }
              );
              
              return { success: true, userId: user.user_id };
            } catch (userError: any) {
              console.error(`Failed to record delivery for user ${user.user_id}:`, userError);
              return { success: false, userId: user.user_id, error: userError.message };
            }
          });

          // Process batch concurrently with Promise.allSettled for error resilience
          const batchResults = await Promise.allSettled(batchPromises);
          return batchResults.map(result => 
            result.status === 'fulfilled' ? result.value : { success: false, error: result.reason }
          );
        }
      );

      // Count successful operations
      const successCount = results.filter(result => result.success).length;
      const failedCount = results.length - successCount;
      
      if (failedCount > 0) {
        console.warn(`${failedCount} message deliveries failed out of ${interestedUsers.length} users`);
      }

      // Record notification campaign in the database
      await this.recordNotificationCampaign(
        project.id,
        'website_message',
        successCount,
        null, // No template data for website messages
        messageText
      );

      console.log(`Successfully processed project release message delivery to ${successCount}/${interestedUsers.length} interested users (batched)`);
      return successCount;
    } catch (error: any) {
      if (error instanceof NotificationValidationError) {
        throw error; // Re-throw validation errors as-is
      }
      
      console.error('Error sending website messages in batch:', error);
      
      // Handle specific error types
      if (error.message?.includes('database') || error.message?.includes('supabase')) {
        throw new NotificationServiceError(
          'Database error while sending website messages. Some messages may not have been sent.',
          'DATABASE_ERROR',
          'sendWebsiteMessagesBatched',
          { originalError: error.message }
        );
      }
      
      if (error.message?.includes('WebsiteMessageService')) {
        throw new NotificationServiceError(
          'Website message service error. Please check the message service configuration.',
          'MESSAGE_SERVICE_ERROR',
          'sendWebsiteMessagesBatched',
          { originalError: error.message }
        );
      }
      
      throw new NotificationServiceError(
        `Failed to send website messages in batch: ${error.message}`,
        'MESSAGE_SENDING_FAILED',
        'sendWebsiteMessagesBatched',
        { originalError: error.message }
      );
    }
  }

  /**
   * Clear cache for performance optimization
   * Requirements: 5.1, 5.2
   */
  static clearCache(): void {
    PostReleaseNotificationService.cache.clear();
  }

  /**
   * Clear specific project cache when project data changes
   * Requirements: 5.1, 5.2
   */
  static clearProjectCache(projectId: string): void {
    PostReleaseNotificationService.cache.delete(`interested_users_${projectId}`);
  }
}

export const postReleaseNotificationService = new PostReleaseNotificationService();